import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Film, Images, Loader2, Star, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { api, type MediaAsset } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { MediaThumb, isMediaVideo, mediaAlt } from "@/components/MediaThumb";
import IgReelImportPanel from "@/components/IgReelImportPanel";

type MediaKind = "image" | "video";

const STATUS_FILTERS = [
  { id: "tagged", label: "do akceptacji", status: "tagged", origin: "" },
  { id: "ready", label: "ready", status: "ready", origin: "" },
  { id: "generated", label: "wygenerowane", status: "", origin: "generated" },
  { id: "queued", label: "kolejka", status: "queued", origin: "" },
  { id: "rejected", label: "odrzucone", status: "rejected", origin: "" },
  { id: "all", label: "wszystkie", status: "", origin: "" },
];

type UploadProgress = {
  current: number;
  total: number;
  name: string;
  done: number;
  failed: number;
  running: boolean;
  errors: string[];
};

export default function Zdjecia() {
  const qc = useQueryClient();
  const [mediaKind, setMediaKind] = useState<MediaKind>("image");
  const [filter, setFilter] = useState("tagged");
  const [q, setQ] = useState("");
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [upload, setUpload] = useState<UploadProgress | null>(null);
  const previewTriggers = useRef<Record<string, HTMLButtonElement | null>>({});
  const active = STATUS_FILTERS.find((row) => row.id === filter) || STATUS_FILTERS[0];
  const listQ = useQuery({
    queryKey: ["media", mediaKind, active.status, active.origin, q],
    queryFn: () =>
      api.mediaList({
        status: active.status,
        origin: active.origin,
        q,
        kind: mediaKind,
      }),
  });
  const scanM = useMutation({
    mutationFn: () => api.mediaScan(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media"] }),
  });
  const uploadM = useMutation({
    mutationFn: async (files: File[]) => {
      const errors: string[] = [];
      let done = 0;
      let failed = 0;
      setUpload({
        current: files.length ? 1 : 0,
        total: files.length,
        name: files[0]?.name || "",
        done: 0,
        failed: 0,
        running: true,
        errors: [],
      });
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        setUpload({
          current: i + 1,
          total: files.length,
          name: file.name,
          done,
          failed,
          running: true,
          errors: [...errors],
        });
        try {
          await api.mediaUpload(file);
          done += 1;
          qc.invalidateQueries({ queryKey: ["media"] });
        } catch (err) {
          failed += 1;
          errors.push(`${file.name}: ${err instanceof Error ? err.message : "błąd"}`);
        }
      }
      setUpload({
        current: files.length,
        total: files.length,
        name: "",
        done,
        failed,
        running: false,
        errors,
      });
      return { ok: done, total: files.length, errors };
    },
  });
  const readyM = useMutation({
    mutationFn: (id: string) => api.mediaReady(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media"] }),
  });
  const retagM = useMutation({
    mutationFn: (id: string) => api.mediaRetag(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media"] }),
  });
  const patchM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<MediaAsset> }) => api.mediaPatch(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media"] }),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.mediaDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media"] }),
  });

  const items = listQ.data?.items || [];
  const preview = items.find((row) => row.id === previewId) || null;
  const isVideoTab = mediaKind === "video";

  const closePreview = () => {
    const id = previewId;
    setPreviewId(null);
    if (id) {
      queueMicrotask(() => previewTriggers.current[id]?.focus());
    }
  };

  const switchKind = (next: MediaKind) => {
    setMediaKind(next);
    setPreviewId(null);
    setQ("");
    if (next === "video" && filter === "generated") setFilter("ready");
  };

  return (
    <div>
      <PageHeader
        title="Media"
        subtitle={
          isVideoTab
            ? "Biblioteka wideo (MP4/MOV). Import z IG poniżej, albo inbox/rolki / upload."
            : "Biblioteka zdjęć. Inbox: inbox/zdjecia. Tła Gemini i klatki z rolek też tu lądują."
        }
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => scanM.mutate()} disabled={scanM.isPending}>
              Skanuj inbox
            </Button>
            <label
              className={cn(
                "inline-flex h-9 cursor-pointer items-center rounded-md border border-input px-3 text-sm",
                uploadM.isPending && "pointer-events-none opacity-50",
              )}
            >
              {uploadM.isPending && upload
                ? `${upload.current}/${upload.total}`
                : isVideoTab
                  ? "Upload wideo"
                  : "Upload zdjęć"}
              <input
                type="file"
                multiple
                accept={isVideoTab ? "video/mp4,video/quicktime,.mp4,.mov" : "image/*"}
                className="sr-only"
                disabled={uploadM.isPending}
                aria-label={isVideoTab ? "Wybierz pliki wideo" : "Wybierz zdjęcia"}
                onChange={(e) => {
                  const files = e.target.files ? Array.from(e.target.files) : [];
                  e.target.value = "";
                  if (files.length) uploadM.mutate(files);
                }}
              />
            </label>
          </div>
        }
      />
      {upload ? <UploadProgressBar progress={upload} /> : null}

      <div
        className="mb-3 inline-flex rounded-lg border border-border bg-muted/40 p-1"
        role="tablist"
        aria-label="Typ mediów"
      >
        <button
          type="button"
          role="tab"
          aria-selected={!isVideoTab}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm",
            !isVideoTab ? "bg-background font-medium text-foreground shadow-sm" : "text-muted-foreground",
          )}
          onClick={() => switchKind("image")}
        >
          <Images className="h-4 w-4" />
          Zdjęcia
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={isVideoTab}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm",
            isVideoTab ? "bg-background font-medium text-foreground shadow-sm" : "text-muted-foreground",
          )}
          onClick={() => switchKind("video")}
        >
          <Film className="h-4 w-4" />
          Wideo
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {STATUS_FILTERS.filter((row) => !(isVideoTab && row.id === "generated")).map((row) => (
          <Button
            key={row.id}
            size="sm"
            variant={filter === row.id ? "brand" : "outline"}
            onClick={() => setFilter(row.id)}
          >
            {row.label}
          </Button>
        ))}
        <Input className="max-w-xs" placeholder="szukaj…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {isVideoTab ? <IgReelImportPanel /> : null}
      {scanM.data ? (
        <p className="mb-3 text-xs text-muted-foreground">
          Nowe: {scanM.data.created || 0}, już były: {scanM.data.reused || 0}, otagowane teraz:{" "}
          {scanM.data.tagged?.done || 0}
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((asset) => (
          <Card key={asset.id}>
            <CardContent className="space-y-2 p-3">
              <button
                type="button"
                ref={(el) => {
                  previewTriggers.current[asset.id] = el;
                }}
                className="flex w-full justify-center overflow-hidden rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label={`Powiększ: ${mediaAlt(asset)}`}
                onClick={() => setPreviewId(asset.id)}
              >
                <MediaThumb asset={asset} />
              </button>
              <div className="flex items-center gap-2 text-sm font-medium">
                {isMediaVideo(asset) ? <Film className="h-4 w-4" /> : <Images className="h-4 w-4" />}
                <span className="truncate">{asset.original_name || asset.id.slice(0, 8)}</span>
                {isMediaVideo(asset) ? (
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">
                    wideo{asset.duration_sec ? ` ${Math.round(asset.duration_sec)}s` : ""}
                  </span>
                ) : null}
                {asset.origin === "generated" ? (
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">
                    Gemini
                  </span>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">{asset.note || "brak notatki"}</p>
              <div className="grid grid-cols-2 gap-2">
                <Select
                  value={asset.usable_as || ""}
                  onChange={(value) => patchM.mutate({ id: asset.id, body: { usable_as: value } })}
                  options={[
                    { value: "", label: "tryb" },
                    { value: "as_is", label: "jak jest" },
                    { value: "background", label: "tło" },
                    { value: "person_ref", label: "osoba" },
                  ]}
                />
                <Select
                  value={asset.funnel_fit || ""}
                  onChange={(value) => patchM.mutate({ id: asset.id, body: { funnel_fit: value } })}
                  options={[
                    { value: "", label: "lejek" },
                    { value: "tofu", label: "TOFU" },
                    { value: "mofu", label: "MOFU" },
                    { value: "bofu", label: "BOFU" },
                  ]}
                />
                <Select
                  value={asset.person || ""}
                  onChange={(value) => patchM.mutate({ id: asset.id, body: { person: value } })}
                  options={[
                    { value: "", label: "osoba" },
                    { value: "kasia", label: "Kasia" },
                    { value: "other", label: "inna" },
                    { value: "none", label: "nikt" },
                  ]}
                />
                <Input
                  key={`${asset.id}-mood`}
                  defaultValue={asset.mood || ""}
                  placeholder="nastrój"
                  onBlur={(e) => patchM.mutate({ id: asset.id, body: { mood: e.target.value } })}
                />
              </div>
              <div className="flex flex-wrap gap-1 text-[11px] text-muted-foreground">
                {(asset.tags || []).map((tag) => (
                  <span key={tag} className="rounded bg-muted px-1.5 py-0.5">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={asset.starred ? "brand" : "outline"}
                  onClick={() => patchM.mutate({ id: asset.id, body: { starred: !asset.starred } })}
                >
                  <Star className={cn("mr-1 h-3.5 w-3.5", asset.starred ? "fill-current" : "")} />
                  {asset.starred ? "Zdejmij" : "Gwiazdka"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (
                      window.confirm(
                        isVideoTab ? "Usunąć to wideo z biblioteki?" : "Usunąć tę grafikę z biblioteki?",
                      )
                    ) {
                      deleteM.mutate(asset.id);
                    }
                  }}
                >
                  <Trash2 className="mr-1 h-3.5 w-3.5" />
                  Usuń
                </Button>
                {asset.status !== "ready" ? (
                  <Button size="sm" variant="brand" onClick={() => readyM.mutate(asset.id)}>
                    Do biblioteki
                  </Button>
                ) : null}
                <Button size="sm" variant="outline" onClick={() => retagM.mutate(asset.id)}>
                  Taguj ponownie
                </Button>
                {asset.status !== "rejected" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => patchM.mutate({ id: asset.id, body: { status: "rejected" } })}
                  >
                    Odrzuć
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {!items.length ? (
        <p className="text-sm text-muted-foreground">
          {isVideoTab
            ? "Pusto. Upload wideo albo wrzuć pliki do inbox/rolki i Skanuj inbox."
            : "Pusto. Upload zdjęć albo wrzuć pliki do inbox/zdjecia i Skanuj inbox."}
        </p>
      ) : null}
      {preview ? <MediaLightbox asset={preview} onClose={closePreview} /> : null}
    </div>
  );
}

function UploadProgressBar({ progress }: { progress: UploadProgress }) {
  const finished = progress.done + progress.failed;
  const pct = progress.total
    ? Math.round((progress.running ? (finished + 0.35) / progress.total : finished / progress.total) * 100)
    : 0;
  const clamped = Math.max(0, Math.min(100, pct));
  const title = progress.running
    ? `Wgrywam ${progress.current} z ${progress.total}`
    : progress.failed
      ? `Wgrano ${progress.done} z ${progress.total}`
      : `Wgrano ${progress.done} z ${progress.total}`;
  return (
    <div
      className="mb-4 rounded-md border border-border bg-muted/30 p-3"
      role="status"
      aria-live="polite"
    >
      <div className="mb-1.5 flex items-center justify-between gap-2 text-xs">
        <span className="flex min-w-0 items-center gap-1.5 font-medium">
          {progress.running ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-brand" /> : null}
          {title}
          {progress.running && progress.name ? (
            <span className="truncate font-normal text-muted-foreground">· {progress.name}</span>
          ) : null}
        </span>
        <span className="shrink-0 tabular-nums text-muted-foreground">{clamped}%</span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={clamped}
        aria-valuetext={`${progress.current} z ${progress.total}${progress.name ? `, ${progress.name}` : ""}`}
      >
        <div className="h-full bg-brand transition-all" style={{ width: `${clamped}%` }} />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        gotowe {progress.done}
        {progress.failed ? ` · błędy ${progress.failed}` : ""}
        {progress.running ? ` · zostało ${Math.max(0, progress.total - finished)}` : ""}
      </p>
      {progress.errors.length ? (
        <p className="mt-1 text-xs text-destructive">{progress.errors.join("; ")}</p>
      ) : null}
    </div>
  );
}

function MediaLightbox({ asset, onClose }: { asset: MediaAsset; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const alt = mediaAlt(asset);
  const video = isMediaVideo(asset);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col gap-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end">
          <Button ref={closeRef} size="sm" variant="outline" onClick={onClose}>
            Zamknij
          </Button>
        </div>
        {video ? (
          <video
            src={api.mediaFile(asset.id, false)}
            className="mx-auto max-h-[80vh] max-w-full rounded-md bg-black object-contain"
            controls
            playsInline
          />
        ) : (
          <img
            src={api.mediaFile(asset.id, false)}
            alt={alt}
            className="mx-auto max-h-[80vh] max-w-full rounded-md object-contain"
          />
        )}
      </div>
    </div>
  );
}
